import { createSlice } from "@reduxjs/toolkit"
import {
  addCandidateInInterview,
  addCandidateViaFileInInterview,
  changeJobTitle,
  createInterviewSchedule,
  deleteInterviewAppointmentCandidate,
  getAssessmentDetails,
  getAssessmentList,
  getInterviewAppointmentCandidates,
  getInterviewQuestions,
  getInterviewsAppointmentInvitedUser,
  getInterviewScheduleDetails,
  getInterviewsScheduleList,
  getMatchedCandidatesList,
  submitInterviewAssessment,
  updateInterviewSchedule,
} from "store/actions/interviewsAction"

const payloadInitialState = {
  limit: 50,
  page_number: 1,
  order_by: 1,
  filters: {
    nationality_id: 0,
    education_qualification_id: 0,
    specialization_id: 0,
    status: -1,
    city_id: 0,
    specialization_ids: [],
    gender: "",
    nationality_ids: [],
    education_qualification_ids: [],
    positions_ids: [],
    ageGroup: { min: null, max: null },
    employee_experience: 0,
    total_capacity: 0,
    employee_experience_as_per_document: 0,
    keyword: "",
    date_range: {},
  },
}

const invitedUserPayloadInitialState = {
  schedule_uuid: "",
  limit: 50,
  page_number: 1,
  order_by: 1,
  filters: {
    type: -1, // -1 All,0: Invited/Pending,1:Accepted, 2:Completed, 3:Cancelled
    keyword: "",
  },
}

const otherTabsPayloadInitialState = {
  limit: 50,
  page_number: 1,
  order_by: 1,
  filters: {
    interview_schedule_id: 0,
    status: 1,
    type: 1,
    keyword: "",
    date_range: [],
    is_trial_run: "all",
    job_title_ids: [],
  },
}

const initialState = {
  loading: false,
  loading2: false,
  loadingInterviewQ: false,
  isLoading: false,
  interviewsList: [],
  assessmentsList: [],
  matchedCandidatesList: [],
  interviewQuestions: [],
  totalAllowTime: null,
  assessmentDetails: {},
  gettingAssessmentDetails: false,
  questions: [],
  perPage: 50,
  totalPages: null,
  currentPage: null,
  totalCount: null,
  interviewAppointmentCandidates: [],
  isLoading3: false,
  interviewAppointmentInvitedUsers: [],
  interviewScheduleDetails: {},
  payload: payloadInitialState,
  invitedUserPayload: invitedUserPayloadInitialState,
  otherTabPayload: otherTabsPayloadInitialState,
  isShowFilter: false,
}

const usersSlice = createSlice({
  name: "interview schedule", //
  initialState,
  reducers: {
    setPayload: (state, action) => {
      state.payload = { ...state.payload, ...action.payload }
    },

    setInvitedUserPayload: (state, action) => {
      state.invitedUserPayload = {
        ...state.invitedUserPayload,
        ...action.payload,
      }
    },
    toggleFilterVisibility: state => {
      state.isShowFilter = !state.isShowFilter
    },

    setOtherTabPayload: (state, action) => {
      state.otherTabPayload = { ...state.otherTabPayload, ...action.payload }
    },

    resetPayload: (state, action) => {
      state.payload = payloadInitialState
    },

    resetOtherTabsPayload: (state, action) => {
      state.otherTabPayload = otherTabsPayloadInitialState
    },

    resetInvitedUserPayload: (state, action) => {
      state.invitedUserPayload = invitedUserPayloadInitialState
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getInterviewsScheduleList.pending, state => {
        state.loading = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(getInterviewsScheduleList.fulfilled, (state, action) => {
        state.loading = false
        const { success, data } = action.payload || {}
        if (success) {
          const { total_records, current_page, total_pages } =
            action.payload || {}
          state.interviewsList = data
          state.totalPages = total_pages
          state.totalCount = total_records
          state.currentPage = current_page
        }
      })
      .addCase(getInterviewsScheduleList.rejected, (state, action) => {
        state.loading = false
      })

    builder
      .addCase(getMatchedCandidatesList.pending, state => {
        state.loading2 = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(getMatchedCandidatesList.fulfilled, (state, action) => {
        state.loading2 = false
        const { success, data } = action.payload || {}
        if (success) {
          const { total_records, current_page, total_pages } =
            action.payload || {}
          state.matchedCandidatesList = data
          state.totalCount = total_records
          state.totalPages = total_pages
          state.totalCount = total_records
          state.currentPage = current_page
        }
      })
      .addCase(getMatchedCandidatesList.rejected, (state, action) => {
        state.loading2 = false
      })

    builder
      .addCase(createInterviewSchedule.pending, state => {
        state.isLoading = true
      })
      .addCase(createInterviewSchedule.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(createInterviewSchedule.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(updateInterviewSchedule.pending, state => {
        state.isLoading = true
      })
      .addCase(updateInterviewSchedule.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(updateInterviewSchedule.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(getInterviewScheduleDetails.pending, state => {
        state.loading = true
        state.interviewScheduleDetails = {}
      })
      .addCase(getInterviewScheduleDetails.fulfilled, (state, action) => {
        state.loading = false
        const { success, data } = action.payload || {}
        if (success) {
          state.interviewScheduleDetails = data
        }
      })
      .addCase(getInterviewScheduleDetails.rejected, (state, action) => {
        state.loading = false
      })

    builder
      .addCase(getAssessmentList.pending, state => {
        state.loading = true
      })
      .addCase(getAssessmentList.fulfilled, (state, action) => {
        state.loading = false
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
        state.assessmentsList = []
        const { success, data } = action.payload || {}
        if (success) {
          const { total_records, current_page, total_pages } =
            action.payload || {}
          state.assessmentsList = data
          state.totalPages = total_pages
          state.totalCount = total_records
          state.currentPage = current_page
        }
      })
      .addCase(getAssessmentList.rejected, (state, action) => {
        state.loading = false
      })

    // GET INTERVIEW QUESTIONS
    builder
      .addCase(getInterviewQuestions.pending, state => {
        state.loadingInterviewQ = true
      })
      .addCase(getInterviewQuestions.fulfilled, (state, action) => {
        state.loadingInterviewQ = false
        const { success, data, total_allow_time } = action.payload || {}
        if (success) {
          state.interviewQuestions = data
          state.totalAllowTime = total_allow_time
        }
      })
      .addCase(getInterviewQuestions.rejected, (state, action) => {
        state.loadingInterviewQ = false
      })

    builder
      .addCase(getAssessmentDetails.pending, state => {
        state.gettingAssessmentDetails = true
        state.assessmentDetails = {}
        state.questions = []
      })
      .addCase(getAssessmentDetails.fulfilled, (state, action) => {
        state.gettingAssessmentDetails = false
        const { success, data, questions } = action.payload || {}
        if (success) {
          state.assessmentDetails = data
          state.questions = questions
        }
      })
      .addCase(getAssessmentDetails.rejected, (state, action) => {
        state.gettingAssessmentDetails = false
      })

    builder
      .addCase(submitInterviewAssessment.pending, state => {
        state.isLoading = true
      })
      .addCase(submitInterviewAssessment.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(submitInterviewAssessment.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(changeJobTitle.pending, state => {
        state.isLoading = true
      })
      .addCase(changeJobTitle.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(changeJobTitle.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(getInterviewAppointmentCandidates.pending, state => {
        state.loading = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(getInterviewAppointmentCandidates.fulfilled, (state, action) => {
        state.loading = false
        const { success, data } = action.payload || {}
        if (success) {
          const { total_records, current_page, total_pages } =
            action.payload || {}
          state.interviewAppointmentCandidates = data
          state.totalPages = total_pages
          state.totalCount = total_records
          state.currentPage = current_page
        }
      })
      .addCase(getInterviewAppointmentCandidates.rejected, (state, action) => {
        state.loading = false
      })

    builder
      .addCase(deleteInterviewAppointmentCandidate.pending, state => {
        state.isLoading = true
      })
      .addCase(
        deleteInterviewAppointmentCandidate.fulfilled,
        (state, action) => {
          state.isLoading = false
        }
      )
      .addCase(
        deleteInterviewAppointmentCandidate.rejected,
        (state, action) => {
          state.isLoading = false
        }
      )

    builder
      .addCase(getInterviewsAppointmentInvitedUser.pending, state => {
        state.isLoading3 = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(
        getInterviewsAppointmentInvitedUser.fulfilled,
        (state, action) => {
          state.isLoading3 = false
          const { success, data } = action.payload || {}
          if (success) {
            const { total_records, current_page, total_pages } =
              action.payload || {}
            state.interviewAppointmentInvitedUsers = data
            state.totalPages = total_pages
            state.totalCount = total_records
            state.currentPage = current_page
          }
        }
      )
      .addCase(
        getInterviewsAppointmentInvitedUser.rejected,
        (state, action) => {
          state.isLoading3 = false
        }
      )

    builder
      .addCase(addCandidateInInterview.pending, state => {
        state.isLoading = true
      })
      .addCase(addCandidateInInterview.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(addCandidateInInterview.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(addCandidateViaFileInInterview.pending, state => {
        state.isLoading = true
      })
      .addCase(addCandidateViaFileInInterview.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(addCandidateViaFileInInterview.rejected, (state, action) => {
        state.isLoading = false
      })
  },
})

export const {
  setPayload,
  setInvitedUserPayload,
  resetInvitedUserPayload,
  setOtherTabPayload,
  resetOtherTabsPayload,
  toggleFilterVisibility,
  resetPayload,
} = usersSlice.actions
export default usersSlice.reducer
